import Head from 'expo-router/head';
import { useCallback, useMemo, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import {
  CityChipRow,
  Faq,
  MoreLink,
  SectionHeading,
  landingStyles as styles,
} from '@/components/riviera/landing-parts';
import { HubFilterStatus, HubFilters, tourFilterGroups } from '@/components/riviera/hub-filters';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { TourCard } from '@/components/riviera/tour-card';
import { Fonts, Spacing } from '@/constants/theme';
import { useCityListingPages } from '@/hooks/use-city-listing-pages';
import { useTheme } from '@/hooks/use-theme';
import { useTours } from '@todo/core/hooks/use-tours';
import {
  DURATION_BUCKETS,
  MIN_TOURS_PER_CITY_PAGE,
  durationBucket,
  tourCityStats,
  type DurationBucket,
  type TourCityStats,
} from '@todo/core/city-listings';
import { cities } from '@/lib/events-data';
import {
  DEFAULT_TOUR_FILTERS,
  applyTourFilters,
  isDefaultFilters,
  type TourDuration,
  type TourFilters,
} from '@todo/core/hub-filters';
import { boatRentalCityCopy } from '@/lib/i18n/boat-rental-content';
import { cityPageCopy } from '@/lib/i18n/city-content';
import { cityEventsCopy, cityInPhrase, cityToursCopy } from '@/lib/i18n/city-listings-content';
import type { Locale } from '@todo/core/i18n/locales';
import { localizedTourTitle } from '@todo/core/i18n/tour-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import { winePageCopy } from '@/lib/i18n/wine-content';
import {
  BOAT_RENTAL_CITIES_SEED_KEY,
  CITY_LISTING_PAGES_SEED_KEY,
  LIST_BUCKET,
  WINE_TOUR_CITIES_SEED_KEY,
  cityToursPageQuery,
  cityToursPageSeedKey,
} from '@todo/core/list-queries';
import {
  SITE_NAME,
  absoluteUrl,
  breadcrumbJsonLd,
  faqPageJsonLd,
  hreflangLinks,
  itemListJsonLd,
  richResultImage,
  seoTitle,
  truncate,
} from '@todo/core/seo';
import { getPrefetched } from '@todo/core/static-prefetch';
import type { Tour } from '@todo/core/tours';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** How many tours the "best-rated" FAQ answer names, and the review floor to be named at all. */
const FAQ_BEST_TOURS = 3;
const FAQ_MIN_REVIEWS = 10;

// Tour prices on this page are EUR only (see tourCityStats).
function eur(value: number): string {
  return `€${Math.round(value)}`;
}

/**
 * The city FAQ, built from the page's own numbers (prices, best-rated tours,
 * durations) so every city's answers differ.
 */
function toursFaq(stats: TourCityStats, tours: Tour[], inCity: string, locale: Locale, t: Translate) {
  if (stats.total === 0) return [];
  const faq: { q: string; a: string }[] = [];

  if (stats.minPrice != null && stats.medianPrice != null) {
    faq.push({
      q: t('cityTours.faq.priceQ', { inCity }),
      a: t('cityTours.faq.priceA', { inCity, min: eur(stats.minPrice), median: eur(stats.medianPrice) }),
    });
  }

  // The RPC orders by tour_rank_score — rating weighted by review count.
  const best = tours
    .filter((tour) => tour.rating != null && (tour.review_count ?? 0) >= FAQ_MIN_REVIEWS)
    .slice(0, FAQ_BEST_TOURS);
  if (best.length > 0) {
    faq.push({
      q: t('cityTours.faq.bestQ', { inCity }),
      a: t('cityTours.faq.bestA', {
        inCity,
        list: best
          .map((tour) =>
            t('cityTours.faq.bestItem', {
              title: localizedTourTitle(tour, locale),
              rating: tour.rating,
              reviews: tour.review_count,
            }),
          )
          .join(t('cityTours.listSeparator')),
      }),
    });
  }

  const durations = DURATION_BUCKETS.filter(
    (bucket) => bucket !== 'unknown' && stats.durations[bucket] > 0,
  );
  if (durations.length > 0) {
    faq.push({
      q: t('cityTours.faq.durationQ', { inCity }),
      a: t('cityTours.faq.durationA', {
        inCity,
        count: stats.total,
        list: durations
          .map((bucket) => `${t(`cityTours.durations.${bucket}`)} — ${stats.durations[bucket]}`)
          .join(t('cityTours.listSeparator')),
      }),
    });
  }

  faq.push({ q: t('cityTours.faq.bookQ', { inCity }), a: t('cityTours.faq.bookA', { inCity }) });
  return faq;
}

/** The /[city]/tours duration group each duration filter option matches. */
const DURATION_FILTER_BUCKETS: Record<TourDuration, DurationBucket | null> = {
  any: null,
  short: 'short',
  half: 'halfDay',
  full: 'fullDay',
};

/**
 * The /[lang]/[city]/tours landing page — every Viator tour departing from one
 * city, grouped by length. Built for "tours in nice" / "excursions nice" /
 * "экскурсии в ницце" searches: the city guide shows 24 tours and the /tours
 * hub lists the whole region (its city chips link here), so the query had no
 * page of its own. Hand-written intro, price/rating/duration
 * numbers and a data-driven FAQ make it more than a filtered list.
 */
export function CityToursScreen({ cityId }: { cityId: string }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const city = cities.find((c) => c.id === cityId);
  const copy = city ? cityToursCopy(locale, city.id) : undefined;
  const inCity = city ? cityInPhrase(locale, city.id) : undefined;

  // Always called (rules of hooks). An unknown city id only arrives via a bad
  // client-side navigation — generateCityToursStaticParams never emits one —
  // and just returns no rows.
  const { tours, loading, error } = useTours(
    cityToursPageQuery(cityId),
    city ? cityToursPageSeedKey(city.id) : undefined,
  );
  const pages = useCityListingPages();
  // Which cities have a wine-tour / boat-rental page, for the cross-links. Read
  // once from the lists baked below; without them the link is just left out.
  const [wineCityIds] = useState(
    () => getPrefetched<string[]>(LIST_BUCKET, WINE_TOUR_CITIES_SEED_KEY) ?? [],
  );
  const [boatCityIds] = useState(
    () => getPrefetched<string[]>(LIST_BUCKET, BOAT_RENTAL_CITIES_SEED_KEY) ?? [],
  );

  // Filters + sort, after hydration only. The seed is already the whole city
  // (with the private flag baked in), so they run in the browser with no
  // request; while any is set, a flat grid of the matches replaces the
  // duration groups.
  const [filters, setFilters] = useState<TourFilters>(DEFAULT_TOUR_FILTERS);
  const isDefault = isDefaultFilters(filters, DEFAULT_TOUR_FILTERS);
  const filteredTours = useMemo(
    () => (isDefault ? null : applyTourFilters(tours, filters)),
    [isDefault, tours, filters],
  );
  const setFilter = useCallback(
    (id: string, value: string) => setFilters((prev) => ({ ...prev, [id]: value })),
    [],
  );
  const resetFilters = useCallback(() => setFilters(DEFAULT_TOUR_FILTERS), []);

  if (!city || !copy || !inCity) {
    return (
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Head key={locale}>
          <title>{`${t('common.notFound')} — ${SITE_NAME}`}</title>
          <meta name="robots" content="noindex, follow" />
        </Head>
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <Text style={[styles.status, { color: theme.mutedForeground, paddingHorizontal: Spacing.three }]}>
          {t('common.notFound')}
        </Text>
      </View>
    );
  }

  const stats = tourCityStats(tours);
  const path = `${city.id}/tours`;
  const canonical = absoluteUrl(locale, path);
  const title = seoTitle(copy.seoTitle);
  const metaDescription = truncate(
    stats.total > 0 && stats.minPrice != null
      ? t('cityTours.metaDescription', { inCity, count: stats.total, price: eur(stats.minPrice) })
      : copy.intro[0],
  );
  const ogImage = richResultImage(tours[0]?.image_url);
  // Too few tours to be worth indexing — see MIN_TOURS_PER_CITY_PAGE. Only
  // once loaded, so a page still fetching client-side isn't flagged.
  const thin = !loading && tours.length < MIN_TOURS_PER_CITY_PAGE;
  const faq = toursFaq(stats, tours, inCity, locale, t);
  const cityGuide = cityPageCopy(city.id, locale);
  const eventsCopy = pages.events.includes(city.id) ? cityEventsCopy(locale, city.id) : undefined;
  const wineCopy = wineCityIds.includes(city.id) ? winePageCopy(locale, city.id) : undefined;
  const boatCopy = boatCityIds.includes(city.id) ? boatRentalCityCopy(locale, city.id) : undefined;

  // Every tour, grouped by length; within a group, best-ranked first (the RPC's order).
  const groups = DURATION_BUCKETS.map((bucket) => ({
    bucket,
    rows: tours.filter((tour) => durationBucket(tour.duration) === bucket),
  })).filter((group) => group.rows.length > 0);

  // Only the durations this city has (stats come from the seed, so the static
  // HTML and the first client render agree).
  const filterGroups = tourFilterGroups(filters, t).map((group) =>
    group.id !== 'duration'
      ? group
      : {
          ...group,
          options: group.options.filter((option) => {
            const bucket = DURATION_FILTER_BUCKETS[option.value as TourDuration];
            // `bucket` is null for "any duration"; a selected option always stays.
            return bucket == null || option.value === group.value || stats.durations[bucket] > 0;
          }),
        },
  );

  const statsLine =
    stats.total > 0
      ? [
          t('cityTours.stats', { count: stats.total }),
          stats.minPrice != null ? t('cityTours.priceStat', { price: eur(stats.minPrice) }) : null,
          stats.avgRating != null ? t('cityTours.ratingStat', { rating: stats.avgRating }) : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        {thin ? <meta name="robots" content="noindex, follow" /> : null}
        <link rel="canonical" href={canonical} />
        {hreflangLinks(path).map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(
            breadcrumbJsonLd(locale, [
              { name: t('nav.cities'), path: 'cities' },
              { name: city.name, path: city.id },
              { name: copy.title },
            ]),
          )}
        </script>
        {tours.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                tours.map((tour) => ({
                  name: localizedTourTitle(tour, locale),
                  url: absoluteUrl(locale, `tours/${tour.id}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        {faq.length > 0 ? (
          <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(faq))}</script>
        ) : null}
        {/* Baked rows so the client's first render matches this static HTML. */}
        {listSeedScript(cityToursPageSeedKey(city.id))}
        {listSeedScript(CITY_LISTING_PAGES_SEED_KEY)}
        {listSeedScript(WINE_TOUR_CITIES_SEED_KEY)}
        {listSeedScript(BOAT_RENTAL_CITIES_SEED_KEY)}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            {/* role="heading" + aria-level renders a real <h1> on web (via
                react-native-web) while staying valid on native. */}
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {copy.title}
            </Text>
            {statsLine ? (
              <Text style={[styles.stats, { color: theme.gold }]}>{statsLine}</Text>
            ) : null}
            {copy.intro.map((paragraph, index) => (
              <Text key={index} style={[styles.intro, { color: theme.text }]}>
                {paragraph}
              </Text>
            ))}

            {pages.tours.length > 1 ? (
              <CityChipRow
                heading={t('cityTours.citiesHeading')}
                chips={pages.tours.map((id) => ({
                  key: id,
                  label: cities.find((c) => c.id === id)?.name ?? id,
                  active: id === city.id,
                  href: { pathname: '/[lang]/[city]/tours', params: { lang: locale, city: id } },
                }))}
              />
            ) : null}

            {loading ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('cityTours.loading')}
              </Text>
            ) : error ? (
              <Text style={[styles.status, { color: theme.destructive }]}>{error}</Text>
            ) : tours.length === 0 ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('cityTours.empty')}
              </Text>
            ) : (
              <>
                <View style={styles.section}>
                  <HubFilters groups={filterGroups} onChange={setFilter} onReset={resetFilters} />
                  <HubFilterStatus
                    filtered={filteredTours != null}
                    total={filteredTours?.length ?? null}
                    loading={false}
                    error={false}
                    countLabel={(count) => t('hubFilters.toursCount', { count })}
                    onRetry={resetFilters}
                    onReset={resetFilters}
                  />
                </View>
                {filteredTours ? (
                  <View style={styles.grid}>
                    {filteredTours.map((tour, index) => (
                      <TourCard key={tour.id} tour={tour} eager={index < 4} />
                    ))}
                  </View>
                ) : (
                  groups.map((group, groupIndex) => (
                    <View key={group.bucket} style={styles.section}>
                      <SectionHeading>
                        {`${t(`cityTours.durations.${group.bucket}`)} (${group.rows.length})`}
                      </SectionHeading>
                      <View style={styles.grid}>
                        {/* The first group's first row is above the fold on desktop — eager, as on /tours. */}
                        {group.rows.map((tour, index) => (
                          <TourCard
                            key={tour.id}
                            tour={tour}
                            eager={groupIndex === 0 && index < 4}
                          />
                        ))}
                      </View>
                    </View>
                  ))
                )}
              </>
            )}

            <Faq heading={t('cityTours.faqHeading', { inCity })} entries={faq} />

            <View style={styles.moreLinks}>
              {cityGuide ? (
                <MoreLink
                  label={cityGuide.title}
                  href={{ pathname: '/[lang]/[city]', params: { lang: locale, city: city.id } }}
                />
              ) : null}
              {eventsCopy ? (
                <MoreLink
                  label={eventsCopy.title}
                  href={{ pathname: '/[lang]/[city]/events', params: { lang: locale, city: city.id } }}
                />
              ) : null}
              {wineCopy ? (
                <MoreLink
                  label={wineCopy.title}
                  href={{ pathname: '/[lang]/wine-tours/[city]', params: { lang: locale, city: city.id } }}
                />
              ) : null}
              {boatCopy ? (
                <MoreLink
                  label={boatCopy.title}
                  href={{ pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: city.id } }}
                />
              ) : null}
              <MoreLink
                label={t('cityTours.allTours')}
                href={{ pathname: '/[lang]/tours', params: { lang: locale } }}
              />
            </View>
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}
